import { useMemo } from "react";
import { BellRing, Info, Receipt, Wallet as WalletIcon } from "lucide-react";
import type { UseQueryResult } from "@tanstack/react-query";
import { formatMoney } from "@empite/api-client";
import type { AlertList, Invoice, InvoiceList, Money, Wallet } from "@empite/api-client";
import { useCustomerApplications } from "../applications";
import type { ApplicationSource, PortalApplication } from "../applications";
import { RECENT_LIMIT, useAlertsAcross, useInvoicesAcross, useWalletsAcross } from "../billing";
import { AppBadge } from "../ui/app-badge";
import type { AppIdentity } from "../ui/app-identity";
import { Button } from "../ui/button";
import { Card, CardHeader } from "../ui/card";
import { DataTable } from "../ui/data-table";
import type { Column } from "../ui/data-table";
import { EmptyState } from "../ui/empty-state";
import { formatTimestamp } from "../ui/format";
import { Notice } from "../ui/notice";
import { PageHeader } from "../ui/page-header";
import { Skeleton } from "../ui/skeleton";
import { StatusPill } from "../ui/status-pill";
import { WalletCard } from "../ui/wallet-card";
import {
  alertPillTone,
  alertReason,
  appIdentity,
  formatMeasure,
  topUpPillTone,
  WalletAlertBanner,
} from "./billing-common";
import type { WalletAlertEntry } from "./billing-common";
import { errorMessage, RequireSession } from "./guards";

/**
 * Wallets — one card per application, because that is one wallet per card.
 *
 * The screen never shows a single "your balance": there is no such number.
 * Credit is held per application and spent only there, so a combined figure
 * would be a total the customer cannot actually spend on anything. The design's
 * two-column grid says the same thing visually — several cards, never one
 * headline figure over them.
 */
export function Wallets() {
  return <RequireSession>{() => <WalletCards />}</RequireSession>;
}

interface WalletRowData {
  application: PortalApplication;
  app: AppIdentity;
  wallet: Wallet | null;
  /** The card's own read, so one unreachable application does not blank the grid. */
  query: UseQueryResult<Wallet | null> | undefined;
}

function WalletCards() {
  const { applications, source, isPending, error } = useCustomerApplications();
  const wallets = useWalletsAcross(applications);
  const invoices = useInvoicesAcross(applications);
  const alerts = useAlertsAcross(applications);

  const rows = useMemo(
    () =>
      applications.map((application, index) => ({
        application,
        app: appIdentity(application, index),
        // A 404 answered `null`, which is "no wallet for this application" and
        // an ordinary card rather than a missing one — the customer has the
        // application, and this card is how they fund it for the first time.
        wallet: wallets[index]?.data ?? null,
        query: wallets[index],
      })),
    [applications, wallets],
  );

  const topUps = useMemo(() => topUpRows(rows, invoices), [rows, invoices]);
  const topUpsPending = invoices.some((query) => query.isPending);

  const alertRows = useMemo(() => alertEntries(rows, alerts), [rows, alerts]);
  // The table is capped; the BANNER is not. An open crossing that fell off the
  // end of a merged page is still a wallet about to stop answering requests,
  // and a cap applied for the sake of a tidy table must not be able to hide it.
  const recentAlerts = useMemo(() => alertRows.slice(0, RECENT_LIMIT), [alertRows]);
  // The applications list is part of it: until it resolves there are no
  // per-application queries to be pending, so `alerts.some()` on an empty array
  // would report "settled" over a screen that has not asked anything yet.
  const alertsPending = isPending || alerts.some((query) => query.isPending);

  return (
    <>
      <PageHeader
        title="Wallets"
        description="Every application you use has its own balance. Top one up, and read what it was spent on."
        actions={
          <Button href="/usage" variant="secondary">
            View usage
          </Button>
        }
      />

      {/* Above the list-failure notice, and above the explainer. Nothing else
          on this screen is more urgent than a wallet that is about to stop
          answering requests, and a banner below two other callouts is one a
          customer scrolls past. */}
      <WalletAlertBanner entries={alertRows} pending={alertsPending} className="mb-5" />

      {error ? (
        <Notice tone="danger" title="We could not list your applications" className="mb-5">
          {errorMessage(error)}
        </Notice>
      ) : null}

      <Notice
        title="Credits belong to one application"
        icon={<Info className="size-4" aria-hidden />}
        className="mb-5"
      >
        Each application has its own wallet, spent only on that application. Balances are
        never transferred, and are not refundable as credit.
      </Notice>

      {isPending ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : applications.length === 0 ? (
        <Card>
          <NoApplications source={source} />
        </Card>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {rows.map((row) => (
            <ApplicationWallet key={row.application.id} row={row} />
          ))}
        </div>
      )}

      {/* Before the receipts, because this table is the history behind the
          banner above: a customer who has just been told a wallet is low reads
          down into when it happened, not into what they last paid. */}
      <Card className="mt-5 overflow-hidden">
        <CardHeader
          title="Wallet alerts"
          description="Every threshold crossing raised against your wallets, newest first."
        />
        <DataTable
          caption="Wallet alerts"
          columns={alertColumns}
          rows={recentAlerts}
          empty={
            <EmptyState
              size="sm"
              icon={<BellRing className="size-5" aria-hidden />}
              title={alertsPending ? "Loading your alerts" : "No alerts"}
              body={
                alertsPending
                  ? undefined
                  : "Nothing has crossed a balance threshold. You are told here, and by email, before a wallet runs out."
              }
            />
          }
          mobileCard={(row) => (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <AppBadge app={row.app} />
                <StatusPill tone={alertPillTone(row.alert.severity)}>
                  {row.alert.severity}
                </StatusPill>
              </div>
              <p className="text-xs text-ink-2">{alertReason(row.alert)}</p>
              <div className="flex items-center justify-between gap-3">
                <span className="tnum text-2xs text-ink-4">
                  {formatTimestamp(row.alert.created_at)}
                </span>
                <span className="tnum text-2xs text-ink-3">
                  {formatMeasure(row.alert.observed)} of {formatMeasure(row.alert.threshold)}
                </span>
              </div>
            </div>
          )}
        />
      </Card>

      <Card className="mt-5 overflow-hidden">
        <CardHeader title="Recent top-ups" description="Across every application." />
        <DataTable
          caption="Recent wallet top-ups"
          columns={topUpColumns}
          rows={topUps}
          empty={
            <EmptyState
              size="sm"
              icon={<Receipt className="size-5" aria-hidden />}
              title={topUpsPending ? "Loading your top-ups" : "No top-ups yet"}
              body={
                topUpsPending
                  ? undefined
                  : "Add credit to an application wallet and the receipt will appear here."
              }
            />
          }
          mobileCard={(row) => (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <AppBadge app={row.app} />
                <span className="tnum font-semibold text-ink">
                  {formatMoney(row.invoice.amount)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="tnum text-2xs text-ink-4">
                  {formatTimestamp(row.invoice.created_at)}
                </span>
                <StatusPill tone={topUpPillTone(row.invoice.status)}>
                  {row.invoice.status}
                </StatusPill>
              </div>
            </div>
          )}
        />
      </Card>
    </>
  );
}

/**
 * One application's card.
 *
 * The design drew a funded wallet and nothing else. Three states reach this
 * card in the real portal — still loading, unreadable, and funded-never — and
 * each one is drawn as the card rather than as a banner beside the grid, so a
 * single failing application cannot take the other cards down with it.
 */
function ApplicationWallet({ row }: { row: WalletRowData }) {
  const { app, query, wallet } = row;

  if (!query || query.isPending) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (query.error) {
    return (
      <Card className="p-5 sm:p-6">
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-display text-lg font-bold text-ink">{app.name}</h3>
            <p className="truncate font-mono text-2xs text-ink-4">{app.slug}</p>
          </div>
          <StatusPill tone="danger" dot>
            unavailable
          </StatusPill>
        </div>
        <Notice tone="danger" title="We could not read this balance" className="mt-4">
          {errorMessage(query.error)}
        </Notice>
      </Card>
    );
  }

  return (
    <WalletCard
      app={app}
      available={wallet?.available ?? zero(wallet)}
      held={wallet?.reserved ?? zero(wallet)}
      // "no wallet" is the honest status for a 404: the application exists and
      // has never been funded, and the card's own "Add credit" button is the
      // answer to it, so there is nothing extra to say.
      status={wallet?.status ?? "no wallet"}
    />
  );
}

/** The amount a wallet-less card shows. The currency is the platform default
 *  only because there is no wallet to read one from. */
function zero(wallet: Wallet | null): Money {
  return { currency: wallet?.currency ?? "USD", micros: "0" };
}

interface TopUpRow {
  /** `Invoice.id` — DataTable keys its rows on this. */
  id: string;
  invoice: Invoice;
  app: AppIdentity;
}

/**
 * Every application's receipts as one list, newest first.
 *
 * Each application was asked for its own `RECENT_LIMIT` rows, so the merge is
 * re-capped: six applications would otherwise stack six pages into a "recent"
 * table, and the oldest rows in it would be older than rows the merge dropped
 * from a busier application.
 */
function topUpRows(
  rows: WalletRowData[],
  queries: Array<UseQueryResult<InvoiceList | null>>,
): TopUpRow[] {
  return rows
    .flatMap((row, index) =>
      (queries[index]?.data?.invoices ?? []).map((invoice) => ({
        id: invoice.id,
        invoice,
        app: row.app,
      })),
    )
    .sort((a, b) => b.invoice.created_at.localeCompare(a.invoice.created_at))
    .slice(0, RECENT_LIMIT);
}

/**
 * Every application's crossings as one list, newest first.
 *
 * Resolved alerts are kept — this table is the history, and a wallet that ran
 * low twice last week is exactly what a customer reading it wants to find.
 * Only the banner filters for open ones.
 *
 * Unlike {@link topUpRows} this is NOT capped: its caller feeds the banner from
 * it, and a row dropped to keep a table tidy would be a warning the customer
 * never saw. The table does its own slicing.
 */
function alertEntries(
  rows: WalletRowData[],
  queries: Array<UseQueryResult<AlertList | null>>,
): WalletAlertEntry[] {
  return rows
    .flatMap((row, index) =>
      (queries[index]?.data?.alerts ?? []).map((alert) => ({
        id: alert.id,
        alert,
        app: row.app,
      })),
    )
    .sort((a, b) => b.alert.created_at.localeCompare(a.alert.created_at))
    .slice(0, RECENT_LIMIT);
}

const alertColumns: Column<WalletAlertEntry>[] = [
  {
    key: "when",
    header: "When",
    className: "w-[13rem]",
    cell: (row) => (
      <span className="tnum whitespace-nowrap">{formatTimestamp(row.alert.created_at)}</span>
    ),
  },
  {
    key: "application",
    header: "Application",
    className: "w-56",
    cell: (row) => <AppBadge app={row.app} />,
  },
  {
    key: "severity",
    header: "Severity",
    className: "w-32",
    cell: (row) => (
      <StatusPill tone={alertPillTone(row.alert.severity)}>{row.alert.severity}</StatusPill>
    ),
  },
  {
    key: "reason",
    header: "What crossed",
    cell: (row) => <span className="text-ink-2">{alertReason(row.alert)}</span>,
  },
  {
    key: "observed",
    header: "Observed",
    className: "w-36",
    // The balance AS IT FIRED, not the wallet's position now. Re-reading the
    // balance here would answer a different question and stop this row
    // agreeing with the email the customer is holding.
    cell: (row) => (
      <span className="tnum font-semibold text-ink">{formatMeasure(row.alert.observed)}</span>
    ),
  },
  {
    key: "threshold",
    header: "Threshold",
    className: "w-36",
    cell: (row) => <span className="tnum text-ink-3">{formatMeasure(row.alert.threshold)}</span>,
  },
  {
    key: "state",
    header: "State",
    className: "w-28",
    cell: (row) =>
      row.alert.resolved_at ? (
        <StatusPill tone="success">resolved</StatusPill>
      ) : (
        <StatusPill tone={alertPillTone(row.alert.severity)} dot>
          open
        </StatusPill>
      ),
  },
];

const topUpColumns: Column<TopUpRow>[] = [
  {
    key: "when",
    header: "When",
    className: "w-[13rem]",
    cell: (row) => (
      <span className="tnum whitespace-nowrap">{formatTimestamp(row.invoice.created_at)}</span>
    ),
  },
  {
    key: "application",
    header: "Application",
    className: "w-56",
    cell: (row) => <AppBadge app={row.app} />,
  },
  {
    key: "amount",
    header: "Amount",
    className: "w-36",
    cell: (row) => (
      <span className="tnum font-semibold text-ink">{formatMoney(row.invoice.amount)}</span>
    ),
  },
  {
    key: "paidWith",
    header: "Paid with",
    className: "w-40",
    cell: (row) => (
      <code className="font-mono text-2xs text-ink-3">{row.invoice.provider}</code>
    ),
  },
  {
    key: "status",
    header: "Status",
    className: "w-32",
    cell: (row) => (
      <StatusPill tone={topUpPillTone(row.invoice.status)}>{row.invoice.status}</StatusPill>
    ),
  },
];

function NoApplications({ source }: { source: ApplicationSource }) {
  return (
    <EmptyState
      icon={<WalletIcon className="size-6" aria-hidden />}
      title="No applications yet"
      body={
        source === "platform"
          ? "A wallet appears here as soon as one of the suite applications opens an account for you. Sign in from a suite application once and its wallet appears here."
          : "The applications configured for this portal did not include any valid entries."
      }
      action={
        source === "platform" ? (
          <Button href="/security" variant="secondary">
            Check your devices
          </Button>
        ) : undefined
      }
    />
  );
}
